import type * as MaplibreGl from "maplibre-gl";
import type { JSX, MutableRefObject, RefObject } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { createAtlasBodies } from "@/components/atlas-bodies";
import type { Camera, Chapter, LocatedCity, ML } from "@/components/atlas-camera";
import {
  bodyLayout,
  cameraAt,
  chapterAt,
  cityCameraAt,
  clamp,
  closingDriftFrame,
  closingFraming,
  closingProgressAt,
  finaleSatelliteOpacity,
  isLocated,
  isMaplibreModule,
  mixCamera,
  openingDecorationOpacity,
  openingFocus,
  rampCatchUp,
  sceneProgressAt,
  timelineDriftFrame,
  WORLD_CENTER,
} from "@/components/atlas-camera";
import { createAtlasCards } from "@/components/atlas-cards";
import type { AtlasLabels } from "@/components/atlas-labels";
import { createAtlasLabels, createCityFacing } from "@/components/atlas-labels";
import type { AtlasMarkers } from "@/components/atlas-markers";
import { createAtlasMarkers } from "@/components/atlas-markers";
import type { AtlasPlanes } from "@/components/atlas-planes";
import { createAtlasPlanes } from "@/components/atlas-planes";
import { CityPostsPanel } from "@/components/CityPostsPanel";
import { afterFirstPaint } from "@/lib/after-first-paint";
import type { LandingCity } from "@/lib/api";

const STYLE_URL = "/vendor/world/style.json";
const VENDOR_BASE = "/vendor/maplibre";
/** Night palette for the paint-override pass: ink background, slate land,
 * near-black water, muted admin hairlines, light coastline. */
const INK = "#07070C";
const LAND = "#242632";
const WATER = "#0D111A";
const BOUNDARY = "#505565";
const COAST = "#C8C8D0";
/** The decorative renderer's palette: the same night colours the map is painted in. */
const BODIES_PALETTE = { surface: LAND, basin: WATER, outline: COAST };
/** Depth of a preview flight. The scroll timeline itself is instant. */
const PREVIEW_MS = 700;
/** A hung style host fires neither load nor error — bound the wait. */
const STYLE_TIMEOUT_MS = 8000;

/**
 * One angular clock: eastward camera-longitude degrees per second, shared by the
 * Earth's spin, the lunar orbital phase and the satellite phase, so the finale
 * advances as a single system instead of three animations that drift apart. The
 * user explicitly asked for a visibly orbiting planet, so this deliberately
 * overrides the plan bullet that the idle page must not run its own rAF chain —
 * the angle is integrated inside the one camera loop and stops the moment a
 * frame it drives becomes ineligible (hidden tab, chapter off the orbit, reduced
 * motion, an in-flight ease). Rate is the user's; the clock itself is not retuned
 * here — the only thing that ever rides beside it is the catch-up below, which is
 * the Earth's own extra angle rather than a second rate for this clock.
 */
const IDLE_SPIN_DEG_PER_SECOND = 0.5;

// MapLibre is served as pre-built ESM from public/vendor (its worker bootstrap
// does not survive bundling) and loaded on demand — nothing map-related lands
// in the app's rollup graph. The specifier is a runtime path outside the
// bundle's module graph, so it cannot become a static import. `ML` and the
// module shape check live in atlas-camera.
type MapState = "loading" | "ready" | "fallback";

/** Exposed so CityIndex rows can put the globe's camera on a city. */
export interface MapHandle {
  flyTo: (cityName: string) => void;
  /**
   * The pointer or focus left the selected city's row: the hover ink goes — the routes fade
   * out, the aircraft return to their resting grey — while the camera keeps framing the city.
   * The ink is the row's own emphasis; the selection is deliberate and stays.
   */
  releaseHighlight: () => void;
}

interface MapSectionProps {
  cities: LandingCity[];
  mapRef?: MutableRefObject<MapHandle | null>;
  sceneRef: RefObject<HTMLDivElement | null>;
}

/**
 * The homepage globe: a self-hosted Natural Earth basemap rendered as a planet
 * (MapLibre v6 `projection: 'globe'`), repainted in the page's night palette and
 * stripped of every basemap label, so the only text on it is our orange city
 * labels. Outside the planet the canvas is transparent.
 *
 * One camera owner: scroll progress through the scene drives a single
 * `map.jumpTo` per animation frame (orbit -> approach -> cities); a CityIndex
 * row rebases the same controller onto a 700ms ease-out flight to that city and
 * also becomes the timeline target, so the selection outlives the flight — the
 * camera never falls back to another city on its own. Scrolling moves the
 * endpoint that ease is already flying toward and keeps that target, so a hover
 * near the directory's exit is eased into rather than replaced by a jump. While
 * the orbit chapter idles with no selection, the same frame also integrates the
 * slow eastward idle drift, and — while the face the camera points at holds no
 * city — a catch-up that brings the next one round in seconds instead of minutes.
 * There is no `map.flyTo`/`map.easeTo` anywhere.
 *
 * Use `?mapstyle=<url>` to temporarily override the style URL for side-by-side
 * comparison.
 */
export default function MapSection({ cities, mapRef, sceneRef }: MapSectionProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  /**
   * The two hover cards' own layer. The cards themselves are created into it by `createAtlasCards`
   * — written imperatively, because a card follows an aircraft that moves every frame and a React
   * state write per pointer event would re-render the whole scene for a decoration — so this
   * element renders no children of its own.
   */
  const cardsLayerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<MapState>("loading");
  /**
   * The city the directory's own selection is on, or null — whose latest posts the panel shows. Owned
   * here because the panel is a React tree and the row's hover is what raises it: the pointer's own
   * cards module never sees it.
   */
  const [panelCity, setPanelCity] = useState<string | null>(null);
  const applyCitiesRef = useRef<((next: LocatedCity[]) => void) | null>(null);

  const located = useMemo(() => cities.filter(isLocated), [cities]);
  // The construction effect runs exactly once (never keyed on the cities), so it
  // reads the live list through a ref rather than through a dependency.
  const locatedRef = useRef(located);
  locatedRef.current = located;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const stage = container.closest<HTMLElement>(".atlas-stage");
    const reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    // Cached once, at setup: `measure` reads these ring boxes, and a per-frame
    // querySelector or layout read would put style resolution and a forced
    // reflow inside every animation frame. The rings are static markup that
    // always exists beside the map, so they are never re-resolved either; the
    // finale's own bodies belong to `createAtlasBodies`, which caches its own.
    const ringOneEl = stage?.querySelector<HTMLElement>(".atlas-ring") ?? null;
    const ringThreeEl = stage?.querySelector<HTMLElement>(".atlas-ring--third") ?? null;
    // The accent the globe paints in (labels, hover aircraft, its routes) is the page's own
    // token, not a second literal that can drift from it. Custom properties inherit, so the
    // container answers for the `.landing-home` declaration above it. An absent token means
    // the page's stylesheet never applied: the globe keeps its geometry and loses only its
    // accent, in the coast hairline's tone, like every other decoration on it.
    const accent = getComputedStyle(container).getPropertyValue("--accent").trim() || COAST;
    // The ink a name takes once it is not the reader's city, read the same way and
    // from the same declaration block: the page's own muted neutral, which the
    // coastline hairlines, the resting aircraft and the inactive directory rows are
    // already drawn in. A stylesheet that never applied leaves the names in the
    // coast tone rather than in a colour invented here.
    const muted = getComputedStyle(container).getPropertyValue("--muted").trim() || COAST;

    let cancelled = false;
    let phase: MapState = "loading";
    let api: ML | null = null;
    let map: MaplibreGl.Map | null = null;
    let markers: AtlasMarkers | null = null;
    let labels: AtlasLabels | null = null;
    let planes: AtlasPlanes | null = null;
    let styleLoaded = false;
    let loadTimeout: number | undefined;
    let frame: number | null = null;
    let sceneTop = 0;
    let citiesTop = 0;
    let closingTop = Number.POSITIVE_INFINITY;
    let docEnd = 0;
    // Half-axes of the two satellite rings, measured once per relayout: the ring
    // border box IS the ellipse its border draws, so these are the satellite
    // offsets' own radii rather than an approximation of the ring path.
    let ringOne: [number, number] = [0, 0];
    let ringThree: [number, number] = [0, 0];
    let chapter: Chapter | null = null;
    let reduced = reducedQuery.matches;
    let targetName: string | null = null;
    let target: [number, number] = WORLD_CENTER;
    // The stage box and the scene's document bounds live here, refreshed by
    // `measure`. The frame path reads these instead of the DOM, so no frame of
    // the animation performs a layout read of its own.
    let stageW = 0;
    let stageH = 0;
    let sceneBottom = 0;
    let previewName: string | null = null;
    // The ease's own start frame and clock stamp. Its destination is not cached:
    // it is re-derived from the live scroll every frame, so scrolling carries the
    // flight along instead of invalidating it. A non-null `previewFrom` IS the
    // in-flight flag; clearing it only ever cancels the ease — the persistent
    // selection (`targetName`/`target` and the lit marker) is deliberately left
    // alone, so a scroll keeps framing the same city instead of dropping it.
    let previewFrom: Camera | null = null;
    let previewStart = 0;
    let spinDeg = 0;
    // The catch-up the Earth carries on top of that clock: how many degrees ahead
    // of it the planet is, and the multiplier currently ramping it there. Only the
    // Earth's own facing longitude reads either — the moon's orbit and the two
    // spacecraft keep the clock, so hurrying a city into view never re-times them.
    let catchUpDeg = 0;
    let catchUp = 1;
    // Whether the face the camera last wrote holds a city. Unknown reads as yes:
    // no map, no payload or no globe transform must leave the drift as it was.
    let cityOnFace = true;
    let cityFacing: ((cities: readonly LocatedCity[]) => boolean) | null = null;
    let spinLast = 0;
    // Whether the previous frame integrated time: the clock banks elapsed time
    // only across a continuously eligible span, never across a gap.
    let spinEligible = false;

    /** The stage owns the diagnostics attributes: absent means the map never mounted. */
    const setPhase = (next: MapState): void => {
      if (phase === "fallback" || cancelled) return;
      phase = next;
      stage?.setAttribute("data-map-state", next);
      setState(next);
    };

    /** The stage's chapter attribute: CSS still keys the marker's closing fade on it. */
    const setChapter = (next: Chapter): void => {
      if (next === chapter) return;
      chapter = next;
      stage?.setAttribute("data-chapter", next);
      // The flight decoration belongs to the cities chapter: that is the chapter where the globe
      // fills the frame and the reader's pointer can leave a row and still be over the planet, so
      // the ink outlives the row's own hover there — and leaves with the chapter, rather than
      // sitting lit through the opening's orbit or the closing finale.
      if (next !== "cities") cards.clear();
    };

    const beatElement = (beat: string): HTMLElement | null =>
      sceneRef.current?.querySelector<HTMLElement>(`[data-atlas-beat="${beat}"]`) ?? null;

    /** Document top of a beat element: cached geometry, never a per-frame read. */
    const docTop = (element: HTMLElement): number =>
      element.getBoundingClientRect().top + window.scrollY;

    /**
     * Cache the scene geometry the scroll timeline is measured from. The closing
     * beat caches as an infinite top when it is absent, so a page without one
     * can never report closing progress.
     */
    const measure = (): void => {
      stageW = stage?.clientWidth || window.innerWidth;
      stageH = stage?.clientHeight || window.innerHeight;
      const scene = sceneRef.current;
      if (!scene) return;
      sceneTop = docTop(scene);
      // The scene's own bottom edge, cached beside its top: `canSpin` asks
      // whether the scene still intersects the viewport, on every frame.
      sceneBottom = sceneTop + scene.offsetHeight;
      const citiesBeat = beatElement("cities");
      citiesTop = citiesBeat ? docTop(citiesBeat) : sceneTop;
      const closingBeat = beatElement("closing");
      closingTop = closingBeat ? docTop(closingBeat) : Number.POSITIVE_INFINITY;
      // Read here, never in the frame loop: scrollHeight forces a synchronous
      // layout, which on every tick would be a reflow per frame.
      docEnd = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      // offsetWidth/offsetHeight, not getBoundingClientRect: these rings are
      // rotated, so a client rect is the rotated envelope, not the ellipse's
      // axes. The unrotated border box is exactly what the border draws.
      if (ringOneEl) ringOne = [ringOneEl.offsetWidth / 2, ringOneEl.offsetHeight / 2];
      if (ringThreeEl) ringThree = [ringThreeEl.offsetWidth / 2, ringThreeEl.offsetHeight / 2];
    };

    measure();

    // Both frames are derived from the measured stage width, so they are
    // initialised only after that first measurement. Declared above `measure`
    // they would be derived from zeroes. The opening frame comes from the
    // displayed payload's city cluster and is deliberately separate from
    // `target`: a row's `flyTo` rebases the timeline, while the cluster only
    // changes when the payload does.
    let openingTarget: [number, number] = openingFocus(locatedRef.current, stageW);
    let displayed = cameraAt(0, target, openingTarget);

    /**
     * The single camera writer. Every frame handed here is already fully
     * composed — the angular drift is part of it — so the camera is written
     * exactly as it arrives. The label copies are aimed from the frame just
     * written, on the same tick: their screen positions are read straight off
     * the transform (`jumpTo` applies it synchronously), so a copy never trails
     * the globe it belongs to.
     */
    const jump = (camera: Camera): void => {
      if (!map) return;
      map.jumpTo(camera);
      displayed = camera;
      labels?.update(locatedRef.current);
      // The fleet's own re-decision, asked only when the camera's zoom has stepped far enough to
      // change a screen distance: the module's clearance floor is measured in pixels, so a frame
      // that only turned the globe cannot change which glyphs survive, while a frame that zoomed
      // can — and the set the pointer is answered from has to be the set this frame paints.
      planes?.update();
      // A card anchors to a projected position on a globe that just turned, so it is re-placed with
      // the camera — and only while one is up: an untouched pointer over empty ocean costs nothing.
      cards.redraw();
      // Whether the face just written holds a city, asked of the camera that landed
      // — the frame the viewer is looking at. `jumpTo` applies the transform
      // synchronously, so the answer belongs to this frame and not the next one.
      cityOnFace = cityFacing?.(locatedRef.current) ?? true;
    };

    /**
     * The one destination for this frame. Both chapters' frames carry the angular
     * drift already — see `closingDriftFrame`/`timelineDriftFrame` — so the ease
     * below interpolates between two frames of the same kind and the drift is
     * never added after the mix. The closing entry and pullback thresholds are
     * unchanged: the finale is not compressed into the last rows' exit. `tick`
     * forces `closing` to 1 on entry in reduced motion, which is what snaps the
     * finale; below that, the explicit selection is the destination itself, so an
     * idle tick or a resize cannot undo a row the reader picked. `drift` is the
     * Earth's own angle for this frame — the clock plus whatever the catch-up has
     * added to it — and the reduced-motion rule belongs to the caller, so this
     * frame never has to ask which clock it is on.
     */
    const frameNow = (
      closing: number,
      p: number,
      height: number,
      diameter: number,
      drift: number,
    ): Camera => {
      if (reduced && closing === 0) {
        return previewName !== null ? cityCameraAt(target) : cameraAt(0, target, openingTarget);
      }
      if (closing > 0) {
        const entry = closingTop - window.innerHeight;
        const from = cameraAt(sceneProgressAt(entry, sceneTop, citiesTop), target, openingTarget);
        return closingDriftFrame(closing, from, target, diameter, height, drift);
      }
      return timelineDriftFrame(p, target, openingTarget, drift);
    };

    /**
     * May the clock integrate this frame? Only where a frame it drives is
     * eligible: a ready globe, an orbit or finale chapter, a scene still in the
     * viewport, no motion reduction, and no *in-flight* ease. A persistent
     * selection (`previewName`) is deliberately not a stop condition — only an
     * actual ease is — so a completed city flight no longer freezes the planet.
     */
    const canSpin = (): boolean =>
      !reduced &&
      !document.hidden &&
      phase === "ready" &&
      previewFrom === null &&
      window.scrollY < sceneBottom &&
      window.scrollY + window.innerHeight > sceneTop &&
      (chapter === "orbit" || chapter === "closing");

    /**
     * Stop the frame the loop is waiting on, if any — the one place that knows how
     * the loop stops, so teardown, a hidden tab and a failed map all stop it the
     * same way.
     */
    const stopFrame = (): void => {
      if (frame === null) return;
      cancelAnimationFrame(frame);
      frame = null;
    };

    function schedule(): void {
      if (cancelled || frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        tick();
      });
    }

    const tick = (): void => {
      const diameter = closingFraming(stageW, stageH).diameter;
      const closingRaw = closingProgressAt(window.scrollY, closingTop, window.innerHeight, docEnd);
      // Reduced motion has no camera animation to run, so the finale snaps to
      // its final frame the moment the section is entered instead of zooming.
      const closing = reduced && closingRaw > 0 ? 1 : closingRaw;
      const p = reduced ? 0 : sceneProgressAt(window.scrollY, sceneTop, citiesTop);
      // The chapter follows the real scroll position even in reduced motion; the
      // finale wins over whatever chapter the timeline would report. It is set
      // before the clock so an eligibility change lands in this same frame.
      setChapter(closing > 0 ? "closing" : chapterAt(p));
      // One angular clock for the Earth and the lunar orbit: integrate only
      // across a continuously eligible span, and rebase the stamp on every other
      // path, so loading, hidden, idle and ineligible time is never banked as a
      // jump. Same increment for the globe's longitude and the orbital phase. The
      // catch-up is integrated beside it, over the same span and by the same rules,
      // but only the orbit chapter may earn it: it exists to bring a city back into
      // view, and the finale's own rotation is not the idle drift's to re-time.
      const now = performance.now();
      const spinOn = canSpin();
      if (spinOn && spinEligible) {
        const seconds = (now - spinLast) / 1000;
        catchUp = rampCatchUp(catchUp, chapter === "orbit" && !cityOnFace, seconds);
        spinDeg = (spinDeg + seconds * IDLE_SPIN_DEG_PER_SECOND) % 360;
        catchUpDeg = (catchUpDeg + seconds * IDLE_SPIN_DEG_PER_SECOND * (catchUp - 1)) % 360;
      }
      spinEligible = spinOn;
      spinLast = now;
      // Written before the map guard: a map that never loaded still gets a
      // correctly sized fallback sphere, the right chapter and the finale art's
      // geometry, so the closing still tracks the scroll without a globe.
      stage?.style.setProperty("--closing-progress", String(closing));
      stage?.style.setProperty("--closing-earth-d", `${diameter}px`);
      // The opening's decorative planes leave with the hero and never return; the
      // spacecraft share that fade on the way out, and come back on a value of
      // their own only once the finale's pullback is established.
      const opening = openingDecorationOpacity(window.scrollY, sceneTop, stageH);
      const satellites = finaleSatelliteOpacity(opening, closing);
      stage?.style.setProperty("--opening-decoration-opacity", String(opening));
      stage?.style.setProperty("--satellite-opacity", String(satellites));
      const layout = bodyLayout(stageW, stageH, diameter, closing, spinDeg, ringOne, ringThree);
      bodies?.render(layout, closing, api);
      // The decorative renderer goes down with the Earth map only when that map
      // is gone for good — a terminal failure or teardown — and only after this
      // frame has written the fallback sphere's geometry. A map that is merely
      // still loading keeps its finale art, and the frame returns before any
      // camera write.
      const mapGone = cancelled || phase === "fallback";
      if (mapGone) bodies?.dispose();
      if (!map) return;
      // Reduced motion has no clock: `canSpin` never advances either angle while it
      // is on, so any value they still hold is history from before the preference
      // matched, and the settled finale must not be rotated by it — the same viewport
      // has to settle on the same frame every time. Normal motion keeps the angle:
      // the settled finale is meant to turn at the lunar rate.
      const earthDrift = reduced ? 0 : (spinDeg + catchUpDeg) % 360;
      // One destination, one writer: the ease interpolates toward the frame the
      // live scroll asks for on this very frame, so scrolling mid-flight carries
      // the flight along instead of replacing it with a jump.
      const destination = frameNow(closing, p, stageH, diameter, earthDrift);
      if (previewFrom !== null && !reduced) {
        const t = clamp((performance.now() - previewStart) / PREVIEW_MS, 0, 1);
        jump(mixCamera(previewFrom, destination, 1 - (1 - t) ** 3));
        // The ease owns its own frames: it must finish even though no angular
        // motion is eligible while it runs. On arrival it clears itself first,
        // so one more frame — the pure timeline frame — resumes eligible rotation
        // with no stale ease left behind.
        if (t === 1) previewFrom = null;
        schedule();
        return;
      }
      jump(destination);
      // One place decides whether the loop lives on: only a frame whose camera
      // is still turning (orbit or finale) keeps it alive. Anything else stops
      // until an event — scroll, resize, visibility, motion, flyTo, load —
      // schedules again.
      if (spinOn) schedule();
    };

    // The finale's decorative bodies — the small lunar globe and the two
    // spacecraft — belong to the renderer: it caches its own nodes and owns its
    // own lunar map, and this controller only hands it a frame and the palette.
    // No stage means no decorations to place; the Earth is unaffected.
    const bodies = stage ? createAtlasBodies(stage, schedule, BODIES_PALETTE) : null;

    const relayout = (): void => {
      measure();
      // A width change across the breakpoint re-derives the phone-adjusted focus,
      // and the padding that parks the sphere in the empty half of the stage.
      openingTarget = openingFocus(locatedRef.current, stageW);
      map?.setPadding(closingFraming(stageW, stageH));
      map?.resize();
      schedule();
    };

    /**
     * Teardown must never mask the terminal state: a map whose WebGL context
     * never came up throws from its own remove(). Map removal is last, so the
     * throw cannot skip any other step.
     */
    const dispose = (): void => {
      const instance = map;
      map = null;
      // Cleared with the map, before the removal below can throw: a torn-down
      // instance must never be asked to project a label again. The labels own a
      // reduced-motion listener of their own, so they are told to let it go rather
      // than merely dropped.
      labels?.dispose();
      labels = null;
      try {
        markers?.dispose();
        markers = null;
        instance?.remove();
      } catch (error) {
        console.error("[noko-map] teardown failed", error);
      } finally {
        // A map whose WebGL context never came up cannot remove its own canvas.
        container.replaceChildren();
      }
    };

    /** Terminal, one-shot: dispose the failed instance and never go back to ready. */
    const fail = (): void => {
      if (cancelled || phase === "fallback") return;
      stopFrame();
      dispose();
      setPhase("fallback");
      // One last frame after disposal: the fallback sphere and the entrance art
      // still need their geometry written, and the chapter still has to track
      // the scroll, so a dead map does not leave the finale parked offscreen.
      // That frame is also where the decorative renderer is disposed, so its
      // geometry write is not skipped.
      schedule();
    };

    /**
     * The style bound only runs while the document is visible. MapLibre cannot
     * complete a first render in a hidden tab, so a clock that kept running
     * there would misread "nobody is painting" as "hung host" and park the page
     * in the terminal fallback before anyone ever saw it. A genuinely hung host
     * still fails after 8s of visible time.
     */
    const armStyleBound = (): void => {
      if (!map || styleLoaded || document.hidden || loadTimeout !== undefined) return;
      loadTimeout = window.setTimeout(() => {
        loadTimeout = undefined;
        if (!styleLoaded) fail();
      }, STYLE_TIMEOUT_MS);
    };

    const disarmStyleBound = (): void => {
      clearTimeout(loadTimeout);
      loadTimeout = undefined;
    };

    /** Data updates never rebuild the map — they repaint it in place. */
    const setCities = (next: LocatedCity[]): void => {
      const kept = next.find((city) => city.name === targetName);
      const pick = kept ?? (next.length > 0 ? next[0] : null);
      if (targetName !== null && !kept) {
        // The selected city vanished — removed, or its coordinates went invalid,
        // which drops it from `next`. Drop the stale ease with it; the marker
        // clears through `reconcileMarkers` below, which re-marks `previewName`.
        previewFrom = null;
        previewName = null;
      }
      targetName = pick ? pick.name : null;
      target = pick ? [pick.lat, pick.lng] : WORLD_CENTER;
      // The opening frame is re-derived from the payload that is actually
      // displayed, so a late or replaced payload re-frames the orbit without a
      // second map and without the camera having to be rebuilt.
      openingTarget = openingFocus(next, stageW);
      if (!map || phase === "fallback") return;
      // Both label layers are repainted from the one owner of their typography;
      // a late payload therefore cannot leave the copies showing old names. The
      // aircraft ride the same payload, and the hover highlight is re-applied with
      // them for the same reason.
      labels?.setCities(next);
      planes?.setCities(next);
      markers?.reconcile(next);
      measure();
      schedule();
    };
    applyCitiesRef.current = setCities;

    const flyTo = (cityName: string): void => {
      const city = locatedRef.current.find((candidate) => candidate.name === cityName);
      if (!city || !map || phase === "fallback") return;
      // A row only owns the camera while the directory itself is on screen. This
      // is user input rather than the frame loop, so a live rect is both cheap and
      // honest about where the row is right now.
      const rect = beatElement("cities")?.getBoundingClientRect();
      if (!rect || rect.top >= window.innerHeight || rect.bottom <= 0) return;
      // Re-armed before the same-city guard below: a release leaves the selection standing with
      // the ink off, so coming back to the row the globe is already framing must light it again.
      planes?.setHighlight(cityName);
      // The names are the third thing the row's own emphasis lights, beside the aircraft and the
      // panel: the selected city keeps its ink while every other name falls back to the page's
      // muted neutral, so the planet's text answers "which city is this about?".
      labels?.setHighlight(cityName);
      // The panel follows the same selection the ink does — the reader is on this city's row, and the
      // globe is drawing its routes — so it is written here rather than by any glyph under the
      // pointer. Both live outside the same-city guard: a row the globe already frames still has to
      // raise its panel and its ink when the pointer comes back to it.
      setPanelCity(cityName);
      // Already the destination: a repeated hover/focus must not re-ease it.
      if (previewName === cityName) return;
      // The city becomes the timeline target (persistent) *and* the destination
      // of the transient ease below, so a completed ease cannot be undone by the
      // next frame's timeline camera. `openingTarget` is deliberately not
      // touched: picking a city must not move where the orbit opens.
      targetName = cityName;
      target = [city.lat, city.lng];
      previewName = cityName;
      // `toggle(token, force)` coerces `force`, so the name comparison is what
      // keeps the class on the selected marker alone — a bare `cityName` would
      // be truthy for every non-empty name and light the whole directory.
      markers?.setActive(cityName);
      // Rebase onto what is on screen now: a newer flight replaces the older one
      // instead of inheriting its endpoint. Reduced motion has no ease to run —
      // `frameNow` reads `previewName` and hands the writer the selected city's
      // own frame — so there is nothing to interpolate from.
      previewFrom = reduced ? null : displayed;
      previewStart = performance.now();
      // The endpoint is not cached: `frameNow` re-derives it from the live scroll
      // every frame, so the scroll-controlled zoom and bearing survive the ease
      // and a finale already pulling back is not forced to the directory's zoom.
      schedule();
    };

    /**
     * The ink's own writer: a no-op until the layer exists, and one write otherwise. The camera
     * is deliberately not touched — the selection outlives the row's hover by design, and only
     * the ink is the row's own emphasis.
     */
    const releaseHighlight = (): void => {
      planes?.setHighlight(null);
      labels?.setHighlight(null);
      setPanelCity(null);
    };

    /**
     * The pointer's own two answers: which glyph is under it — the aircraft and airport hover inks,
     * the card that explains it — and where that card may sit. The cards live in a module of their
     * own, created once into the layer the page renders for them; the ink and the row's own emphasis
     * are written here, where the layers are, so the card module never touches a map. The globe is
     * never handed a pointer event either — the module reads the pointer on the window, because
     * `.atlas-map` is `pointer-events: none` on purpose and the rows above it are the real targets.
     */
    const cards = createAtlasCards({
      layer: cardsLayerRef.current,
      box: () => containerRef.current?.getBoundingClientRect() ?? null,
      markers: () => markers?.list() ?? [],
      hitTest: (point) => planes?.hitTest(point) ?? null,
      project: (coordinates) => map?.project(coordinates) ?? null,
      ink: (hit) => planes?.setPortHover(hit?.kind === "port" ? hit.key : null),
      release: releaseHighlight,
    });

    const onScroll = (): void => {
      // Scrolling moves the endpoint the ease is already interpolating toward;
      // it cancels neither that ease nor the selected city. The clock needs no
      // re-arm — `tick` integrates whenever it is eligible.
      schedule();
    };

    const onVisibility = (): void => {
      if (document.hidden) {
        // A hidden tab cannot paint: pause the style clock, the camera frame and
        // the decorative renderer. The drift needs no separate stop — `canSpin`
        // refuses while the document is hidden, and no frame can run here to
        // integrate hidden time.
        disarmStyleBound();
        bodies?.pause();
        stopFrame();
        map?.stop();
        return;
      }
      // Resume by synchronising to the current scroll, never by replaying time:
      // an unfinished ease restarts from what is displayed, with a fresh stamp.
      armStyleBound();
      if (previewFrom !== null) {
        previewFrom = displayed;
        previewStart = performance.now();
      }
      spinLast = performance.now();
      schedule();
    };

    const onMotion = (): void => {
      reduced = reducedQuery.matches;
      // The two motion modes have different destinations, so an ease in flight
      // across the change is dropped rather than finished against a stale one.
      previewFrom = null;
      schedule();
    };

    const observer = new ResizeObserver(relayout);
    observer.observe(container);
    if (stage) observer.observe(stage);
    const scene = sceneRef.current;
    if (scene) observer.observe(scene);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", relayout);
    document.addEventListener("visibilitychange", onVisibility);
    reducedQuery.addEventListener("change", onMotion);
    setChapter(chapterAt(sceneProgressAt(window.scrollY, sceneTop, citiesTop)));
    stage?.setAttribute("data-map-state", "loading");

    void (async () => {
      // The document already painted the directory and the atlas art; the globe's
      // own bundle (258 KB of transfer) starts only once the browser is idle, so it
      // never competes with that paint.
      await afterFirstPaint();
      if (cancelled) return;
      let vendor: unknown;
      try {
        // oxlint-disable-next-line noko-lint/no-inline-import -- deliberate lazy code-split of the vendored maplibre ESM (NOKO-415)
        vendor = await import(/* @vite-ignore */ `${VENDOR_BASE}/maplibre-gl.mjs`);
      } catch (error) {
        console.error("[noko-map] vendored maplibre failed to load", error);
        fail();
        return;
      }
      if (cancelled) return;
      if (!isMaplibreModule(vendor)) {
        fail();
        return;
      }
      api = vendor;

      if (!document.querySelector(`link[href="${VENDOR_BASE}/maplibre-gl.css"]`)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = `${VENDOR_BASE}/maplibre-gl.css`;
        document.head.appendChild(link);
      }

      try {
        map = new vendor.Map({
          container,
          // ?mapstyle= overrides the basemap style URL (local verification only).
          style: new URLSearchParams(window.location.search).get("mapstyle") ?? STYLE_URL,
          // The opening frame, camera for camera the one `displayed` starts from.
          ...cameraAt(0, target, openingTarget),
          minZoom: -2,
          // Display-only: the map takes no wheel, touch, drag or keyboard input.
          interactive: false,
          // Natural Earth is public domain; no attribution is required.
          attributionControl: false,
        });
      } catch (error) {
        console.error("[noko-map] map construction failed", error);
        fail();
        return;
      }

      const instance = map;
      instance.on("error", (event) => {
        if (phase === "fallback") return;
        console.error("[noko-map]", event.error?.message ?? event);
        fail();
      });
      instance.getCanvas().addEventListener("webglcontextlost", fail);

      // The style fetch starts here: the bound is armed only if the tab is
      // visible, and otherwise waits for the next visibilitychange.
      armStyleBound();

      instance.on("load", () => {
        styleLoaded = true;
        disarmStyleBound();
        if (cancelled || !map || !api) return;

        // Globe mode — must wait for the style: setProjection throws while
        // the style is still loading (v6 _checkLoaded).
        map.setProjection({ type: "globe" });

        // De-noise to a bare planet: hide every basemap feature layer and
        // repaint what remains in the page's night palette. Our orange labels
        // are added below, after this pass.
        const HIDE =
          /road|tunnel|bridge|building|poi|transit|aeroway|rail|ferry|neighbou?rhood|locality|suburb|quarter|hamlet|village|place_/i;
        for (const layer of map.getStyle().layers) {
          if (HIDE.test(layer.id)) map.setLayoutProperty(layer.id, "visibility", "none");
          if (layer.type === "background") {
            map.setPaintProperty(layer.id, "background-color", INK);
          } else if (layer.type === "fill" && /water|lake/.test(layer.id)) {
            map.setPaintProperty(layer.id, "fill-color", WATER);
          } else if (layer.type === "fill" && /land/.test(layer.id)) {
            map.setPaintProperty(layer.id, "fill-color", LAND);
          } else if (layer.type === "line" && /boundary|admin/.test(layer.id)) {
            map.setPaintProperty(layer.id, "line-color", BOUNDARY);
            map.setPaintProperty(layer.id, "line-opacity", 0.5);
          }
        }

        // A line layer over the land polygons traces their rings, so the
        // sphere gets a crisp silhouette without any new data.
        map.addLayer({
          id: "noko-coast",
          type: "line",
          source: "land",
          paint: {
            "line-color": COAST,
            "line-opacity": 0.55,
            "line-width": ["interpolate", ["linear"], ["zoom"], 0, 0.4, 3, 0.8, 6, 1.4, 10, 2.2],
          },
        });

        // The aircraft go on before the names, and the layer order is the whole
        // reason: a symbol layer added later paints above, so the city text stays
        // on top of every plane. They ride the same payload the labels do.
        planes = createAtlasPlanes(map, accent);
        planes.setCities(locatedRef.current);

        // Orange name labels — the only text on the planet. Both layers, the
        // facing names and the dimmed copies of the cities behind the planet,
        // come from the one owner of their typography.
        labels = createAtlasLabels(map, locatedRef.current, accent, muted);
        cityFacing = createCityFacing(instance);

        // The bars own a marker each, so the layer is built with the map it projects onto.
        markers = createAtlasMarkers(instance, api);
        markers.reconcile(locatedRef.current);
        map.setPadding(closingFraming(stageW, stageH));
        measure();
        setPhase("ready");
        schedule();
      });

      if (mapRef) mapRef.current = { flyTo, releaseHighlight };
    })();

    return () => {
      cancelled = true;
      stopFrame();
      clearTimeout(loadTimeout);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", relayout);
      document.removeEventListener("visibilitychange", onVisibility);
      reducedQuery.removeEventListener("change", onMotion);
      // The cards own the pointer listeners and their own nodes, so they go first: nothing may
      // answer a pointer with a card that is on its way out.
      cards.dispose();
      observer.disconnect();
      dispose();
      // The aircraft own an interval and a source of their own, so they go with
      // the map rather than outliving it.
      planes?.dispose();
      planes = null;
      // The decorative renderer owns a second MapLibre map of its own, so it is
      // torn down with this one: teardown is the last write this effect makes.
      bodies?.dispose();
      if (applyCitiesRef.current === setCities) applyCitiesRef.current = null;
      if (mapRef) mapRef.current = null;
    };
  }, [mapRef, sceneRef]);

  useEffect(() => {
    applyCitiesRef.current?.(located);
  }, [located]);

  return (
    <>
      <div className="atlas-map" data-map-state={state} style={{ pointerEvents: "none" }}>
        <div
          ref={containerRef}
          className="atlas-map-canvas"
          aria-hidden="true"
          style={{ width: "100%", height: "100%" }}
        />
        {/* The cards' own layer: `createAtlasCards` fills it, so it renders no children of its own. */}
        <div ref={cardsLayerRef} className="atlas-cards" aria-hidden="true" />
      </div>
      {/* Beside the map rather than inside it: `.atlas-map` is one stacking context and the pointer's
          cards live in it, while the panel has to paint over the vignette and the orbital rings that
          sit above the canvas. It is the reader's own corner of the stage, not part of the planet. */}
      <CityPostsPanel
        city={panelCity === null ? null : (located.find((sky) => sky.name === panelCity) ?? null)}
      />
    </>
  );
}
